import { createApiService, type ApiError } from './api';
import type {
  Skill,
  Occupation,
  PainPoint,
  Win,
  PaginatedResponse,
} from '@/types/skillKnowledge';
import { unwrapPaginatedResponse } from '@/utils/pagination';
import type { ApiResponse } from '@/types/common';

// Skill Knowledge Management Service API Base URL
// Uses nginx reverse proxy path for all environments (K8s, Docker, local via Vite)
const SKILL_KNOWLEDGE_API_BASE_URL =
  import.meta.env.VITE_SKILL_KNOWLEDGE_API_BASE_URL || '/api/skill-knowledge';

const skillKnowledgeApiService = createApiService(SKILL_KNOWLEDGE_API_BASE_URL);

/**
 * Unwrap a single entity response from ApiResponse wrapper
 */
function unwrapEntityResponse<T>(response: unknown): T {
  if (response && typeof response === 'object' && 'success' in response) {
    const wrapped = response as ApiResponse<T>;
    if (wrapped.success && wrapped.data !== undefined) {
      return wrapped.data;
    }
    if (!wrapped.success) {
      throw new Error(wrapped.message || 'API request failed');
    }
  }
  // If not wrapped, return as-is (e.g., Skills API returns directly)
  return response as T;
}

/**
 * Transform PainPoint response from API format to our expected format
 * API returns 'wins' array, but our type expects 'winIds' array
 */
function transformPainPointResponse(apiResponse: unknown): PainPoint {
  const painPoint = unwrapEntityResponse<PainPoint & { wins?: Win[] }>(apiResponse);
  
  // Extract win IDs from wins array if present
  if (painPoint.wins && Array.isArray(painPoint.wins) && !painPoint.winIds) {
    painPoint.winIds = painPoint.wins.map(win => win.id).filter((id): id is string => !!id);
  }
  
  // Remove wins array from response as it's not in our type
  const { wins: _wins, ...transformed } = painPoint;
  return transformed as PainPoint;
}

// Skill Management APIs
export const skillApi = {
  // Create skill
  create: async (data: Omit<Skill, 'id' | 'createdAt' | 'updatedAt'>): Promise<Skill> => {
    const response = await skillKnowledgeApiService.post<unknown>('/api/v1/skills', data);
    return unwrapEntityResponse<Skill>(response);
  },

  // Get skill by ID
  getById: async (skillId: string): Promise<Skill> => {
    try {
      // Skills API may return SkillResponse directly or wrapped
      const response = await skillKnowledgeApiService.get<unknown>(`/api/v1/skills/${skillId}`);
      return unwrapEntityResponse<Skill>(response);
    } catch (error: unknown) {
      console.error(`Error fetching skill ${skillId}:`, error);
      throw error;
    }
  },

  // List skills with pagination
  // API uses: page (0-indexed), size, sortBy, sortDirection
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: string;
    category?: string;
    domain?: string;
    status?: string;
    isActive?: boolean;
    difficulty?: number;
  }): Promise<PaginatedResponse<Skill>> => {
    const queryParams = new URLSearchParams();
    // Spring Boot pagination uses 0-indexed pages
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    
    // Handle sort parameters - API expects separate sortBy and sortDirection
    if (params?.sortBy) {
      queryParams.append('sortBy', params.sortBy);
      queryParams.append('sortDirection', (params?.sortDirection || 'asc').toLowerCase());
    }
    
    // Filter parameters
    if (params?.category) queryParams.append('category', params.category);
    if (params?.domain) queryParams.append('domain', params.domain);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params?.difficulty !== undefined) queryParams.append('difficulty', params.difficulty.toString());

    const query = queryParams.toString();
    const endpoint = `/api/v1/skills${query ? `?${query}` : ''}`;
    
    try {
      // Skills API returns PageSkillResponse directly (not wrapped in ApiResponse)
      const response = await skillKnowledgeApiService.get<unknown>(endpoint);
      return unwrapPaginatedResponse<Skill>(response);
    } catch (error: unknown) {
      const apiError = error as { message?: string; status?: number; details?: unknown };
      console.error('[skillApi.list] Error fetching skills from API:', {
        endpoint,
        error: apiError.message,
        status: apiError.status,
        details: apiError.details,
      });
      throw error;
    }
  },

  // Update skill
  update: async (skillId: string, data: Partial<Skill>): Promise<Skill> => {
    const response = await skillKnowledgeApiService.put<unknown>(`/api/v1/skills/${skillId}`, data);
    return unwrapEntityResponse<Skill>(response);
  },

  // Delete skill
  delete: async (skillId: string): Promise<void> => {
    return skillKnowledgeApiService.delete<void>(`/api/v1/skills/${skillId}`);
  },

  // Activate skill
  activate: async (skillId: string): Promise<Skill> => {
    const response = await skillKnowledgeApiService.patch<unknown>(`/api/v1/skills/${skillId}/activate`, {});
    return unwrapEntityResponse<Skill>(response);
  },

  // Deactivate skill
  deactivate: async (skillId: string): Promise<Skill> => {
    const response = await skillKnowledgeApiService.patch<unknown>(`/api/v1/skills/${skillId}/deactivate`, {});
    return unwrapEntityResponse<Skill>(response);
  },

  // Map skill to content
  mapContent: async (skillId: string, contentId: string): Promise<void> => {
    return skillKnowledgeApiService.post<void>(`/api/v1/skills/${skillId}/content/${contentId}/map`, {});
  },

  // Unmap skill from content
  unmapContent: async (skillId: string, contentId: string): Promise<void> => {
    return skillKnowledgeApiService.delete<void>(`/api/v1/skills/${skillId}/content/${contentId}/unmap`);
  },
};

// Occupation Management APIs
export const occupationApi = {
  // Create occupation
  create: async (data: Omit<Occupation, 'id' | 'createdAt' | 'updatedAt'>): Promise<Occupation> => {
    const response = await skillKnowledgeApiService.post<unknown>('/api/v1/occupations', data);
    return unwrapEntityResponse<Occupation>(response);
  },

  // Get occupation by ID
  getById: async (id: string): Promise<Occupation> => {
    const response = await skillKnowledgeApiService.get<unknown>(`/api/v1/occupations/${id}`);
    return unwrapEntityResponse<Occupation>(response);
  },

  // List occupations with pagination
  // API uses: page (0-indexed), size, sortBy, sortDirection
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: string;
    industry?: string;
    careerLevel?: string;
    occupationType?: string;
    growthOutlook?: string;
  }): Promise<PaginatedResponse<Occupation>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    
    // Handle sort parameters - API expects separate sortBy and sortDirection
    if (params?.sortBy) {
      queryParams.append('sortBy', params.sortBy);
      queryParams.append('sortDirection', (params?.sortDirection || 'asc').toLowerCase());
    }
    
    if (params?.industry) queryParams.append('industry', params.industry);
    if (params?.careerLevel) queryParams.append('careerLevel', params.careerLevel);
    if (params?.occupationType) queryParams.append('occupationType', params.occupationType);
    if (params?.growthOutlook) queryParams.append('growthOutlook', params.growthOutlook);

    const query = queryParams.toString();
    const endpoint = `/api/v1/occupations${query ? `?${query}` : ''}`;
    
    try {
      // Occupations API returns ApiResponsePageOccupationResponse (wrapped)
      const response = await skillKnowledgeApiService.get<unknown>(endpoint);
      return unwrapPaginatedResponse<Occupation>(response);
    } catch (error: unknown) {
      const apiError = error as { message?: string; status?: number };
      console.error('[occupationApi.list] Error fetching occupations:', {
        endpoint,
        error: apiError.message,
        status: apiError.status,
      });
      throw error;
    }
  },

  // Update occupation
  update: async (id: string, data: Partial<Occupation>): Promise<Occupation> => {
    const response = await skillKnowledgeApiService.put<unknown>(`/api/v1/occupations/${id}`, data);
    return unwrapEntityResponse<Occupation>(response);
  },

  // Delete occupation
  delete: async (id: string): Promise<void> => {
    return skillKnowledgeApiService.delete<void>(`/api/v1/occupations/${id}`);
  },

    // Search occupations
  search: async (query: string, params?: { page?: number; size?: number }): Promise<PaginatedResponse<Occupation>> => {
    const queryParams = new URLSearchParams();
    queryParams.append('query', query);
    // API uses pageable query param format for search
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    try {
      const response = await skillKnowledgeApiService.get<unknown>(
        `/api/v1/occupations/search?${queryParams.toString()}`
      );
      return unwrapPaginatedResponse<Occupation>(response);
    } catch (error: unknown) {
      console.error('[occupationApi.search] Error searching occupations:', error);
      throw error;
    }
  },

  // Get occupations by industry
  getByIndustry: async (industry: string, params?: { page?: number; size?: number }): Promise<PaginatedResponse<Occupation>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    try {
      const response = await skillKnowledgeApiService.get<unknown>(
        `/api/v1/occupations/industry/${industry}${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Occupation>(response);
    } catch (error: unknown) {
      console.error('[occupationApi.getByIndustry] Error fetching occupations:', error);
      throw error;
    }
  },

  // Get occupations by career level
  getByCareerLevel: async (level: string, params?: { page?: number; size?: number }): Promise<PaginatedResponse<Occupation>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    try {
      const response = await skillKnowledgeApiService.get<unknown>(
        `/api/v1/occupations/career-level/${level}${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Occupation>(response);
    } catch (error: unknown) {
      console.error('[occupationApi.getByCareerLevel] Error fetching occupations:', error);
      throw error;
    }
  },

  // Get occupations by skill
  getBySkill: async (skillId: string, params?: { page?: number; size?: number }): Promise<PaginatedResponse<Occupation>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    try {
      const response = await skillKnowledgeApiService.get<unknown>(
        `/api/v1/occupations/skills/${skillId}${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Occupation>(response);
    } catch (error: unknown) {
      console.error('[occupationApi.getBySkill] Error fetching occupations:', error);
      throw error;
    }
  },
};

// Pain Point Management APIs
export const painPointApi = {
  // Create pain point
  create: async (data: Omit<PainPoint, 'id' | 'createdAt' | 'updatedAt'>): Promise<PainPoint> => {
    const response = await skillKnowledgeApiService.post<unknown>('/api/v1/pain-points', data);
    return transformPainPointResponse(response);
  },

  // Get pain point by ID
  getById: async (id: string): Promise<PainPoint> => {
    const response = await skillKnowledgeApiService.get<unknown>(`/api/v1/pain-points/${id}`);
    return transformPainPointResponse(response);
  },

  // List pain points with pagination
  // API uses: page (0-indexed), size, sortBy, sortDirection
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: string;
    isActive?: boolean;
    skillId?: string;
  }): Promise<PaginatedResponse<PainPoint>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    
    // Handle sort parameters - API expects separate sortBy and sortDirection
    if (params?.sortBy) {
      queryParams.append('sortBy', params.sortBy);
      queryParams.append('sortDirection', (params?.sortDirection || 'asc').toLowerCase());
    }
    
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params?.skillId) queryParams.append('skillId', params.skillId);

    const query = queryParams.toString();
    const endpoint = `/api/v1/pain-points${query ? `?${query}` : ''}`;
    
    try {
      // Pain Points API returns ApiResponsePagePainPointResponse (wrapped)
      const response = await skillKnowledgeApiService.get<unknown>(endpoint);
      const paginatedResponse = unwrapPaginatedResponse<PainPoint & { wins?: Win[] }>(response);
      // Transform each pain point to extract winIds from wins array
      return {
        ...paginatedResponse,
        content: paginatedResponse.content.map(pp => {
          const { wins, ...rest } = pp;
          if (wins && Array.isArray(wins) && !rest.winIds) {
            rest.winIds = wins.map(win => win.id).filter((id): id is string => !!id);
          }
          return rest as PainPoint;
        }),
      };
    } catch (error: unknown) {
      const apiError = error as { message?: string; status?: number };
      console.error('[painPointApi.list] Error fetching pain points:', {
        endpoint,
        error: apiError.message,
        status: apiError.status,
      });
      throw error;
    }
  },

  // Update pain point
  update: async (id: string, data: Partial<PainPoint>): Promise<PainPoint> => {
    const response = await skillKnowledgeApiService.patch<unknown>(`/api/v1/pain-points/${id}`, data);
    return transformPainPointResponse(response);
  },

  // Delete pain point
  delete: async (id: string): Promise<void> => {
    return skillKnowledgeApiService.delete<void>(`/api/v1/pain-points/${id}`);
  },

  // Search pain points
  search: async (query: string, params?: { page?: number; size?: number }): Promise<PaginatedResponse<PainPoint>> => {
    const queryParams = new URLSearchParams();
    queryParams.append('query', query);
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    try {
      const response = await skillKnowledgeApiService.get<unknown>(
        `/api/v1/pain-points/search?${queryParams.toString()}`
      );
      const paginatedResponse = unwrapPaginatedResponse<PainPoint & { wins?: Win[] }>(response);
      // Transform each pain point to extract winIds from wins array
      return {
        ...paginatedResponse,
        content: paginatedResponse.content.map(pp => {
          const { wins, ...rest } = pp;
          if (wins && Array.isArray(wins) && !rest.winIds) {
            rest.winIds = wins.map(win => win.id).filter((id): id is string => !!id);
          }
          return rest as PainPoint;
        }),
      };
    } catch (error: unknown) {
      console.error('[painPointApi.search] Error searching pain points:', error);
      throw error;
    }
  },

  // Activate pain point
  activate: async (id: string): Promise<PainPoint> => {
    const response = await skillKnowledgeApiService.patch<unknown>(`/api/v1/pain-points/${id}/activate`, {});
    return transformPainPointResponse(response);
  },

  // Deactivate pain point
  deactivate: async (id: string): Promise<PainPoint> => {
    const response = await skillKnowledgeApiService.patch<unknown>(`/api/v1/pain-points/${id}/deactivate`, {});
    return transformPainPointResponse(response);
  },

  // Get pain points by skill
  getBySkill: async (skillId: string, params?: { page?: number; size?: number }): Promise<PaginatedResponse<PainPoint>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    try {
      const response = await skillKnowledgeApiService.get<unknown>(
        `/api/v1/pain-points/by-skill/${skillId}${query ? `?${query}` : ''}`
      );
      const paginatedResponse = unwrapPaginatedResponse<PainPoint & { wins?: Win[] }>(response);
      // Transform each pain point to extract winIds from wins array
      return {
        ...paginatedResponse,
        content: paginatedResponse.content.map(pp => {
          const { wins, ...rest } = pp;
          if (wins && Array.isArray(wins) && !rest.winIds) {
            rest.winIds = wins.map(win => win.id).filter((id): id is string => !!id);
          }
          return rest as PainPoint;
        }),
      };
    } catch (error: unknown) {
      console.error('[painPointApi.getBySkill] Error fetching pain points:', error);
      throw error;
    }
  },
};

// Win Management APIs
export const winApi = {
  // Create win
  create: async (data: Omit<Win, 'id' | 'createdAt' | 'updatedAt'>): Promise<Win> => {
    const response = await skillKnowledgeApiService.post<unknown>('/api/v1/wins', data);
    return unwrapEntityResponse<Win>(response);
  },

  // Get win by ID
  getById: async (id: string): Promise<Win> => {
    const response = await skillKnowledgeApiService.get<unknown>(`/api/v1/wins/${id}`);
    return unwrapEntityResponse<Win>(response);
  },

  // List wins with pagination
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: string;
    isActive?: boolean;
    skillId?: string;
  }): Promise<PaginatedResponse<Win>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    
    // Handle sort parameters - API expects separate sortBy and sortDirection
    if (params?.sortBy) {
      queryParams.append('sortBy', params.sortBy);
      queryParams.append('sortDirection', (params?.sortDirection || 'asc').toLowerCase());
    }
    
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params?.skillId) queryParams.append('skillId', params.skillId);

    const query = queryParams.toString();
    const endpoint = `/api/v1/wins${query ? `?${query}` : ''}`;
    
    try {
      // Wins API returns ApiResponsePageWinResponse (wrapped)
      const response = await skillKnowledgeApiService.get<unknown>(endpoint);
      return unwrapPaginatedResponse<Win>(response);
    } catch (error: unknown) {
      const apiError = error as { message?: string; status?: number };
      console.error('[winApi.list] Error fetching wins:', {
        endpoint,
        error: apiError.message,
        status: apiError.status,
      });
      throw error;
    }
  },

  // Update win
  update: async (id: string, data: Partial<Win>): Promise<Win> => {
    const response = await skillKnowledgeApiService.put<unknown>(`/api/v1/wins/${id}`, data);
    return unwrapEntityResponse<Win>(response);
  },

  // Delete win
  delete: async (id: string): Promise<void> => {
    return skillKnowledgeApiService.delete<void>(`/api/v1/wins/${id}`);
  },

  // Search wins
  search: async (query: string, params?: { page?: number; size?: number }): Promise<PaginatedResponse<Win>> => {
    const queryParams = new URLSearchParams();
    queryParams.append('name', query); // API uses 'name' parameter, not 'query'
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    try {
      const response = await skillKnowledgeApiService.get<unknown>(
        `/api/v1/wins/search?${queryParams.toString()}`
      );
      return unwrapPaginatedResponse<Win>(response);
    } catch (error: unknown) {
      console.error('[winApi.search] Error searching wins:', error);
      throw error;
    }
  },

  // Activate win - API uses POST, not PATCH
  activate: async (id: string): Promise<Win> => {
    const response = await skillKnowledgeApiService.post<unknown>(`/api/v1/wins/${id}/activate`, {});
    return unwrapEntityResponse<Win>(response);
  },

  // Deactivate win - API uses POST, not PATCH
  deactivate: async (id: string): Promise<Win> => {
    const response = await skillKnowledgeApiService.post<unknown>(`/api/v1/wins/${id}/deactivate`, {});
    return unwrapEntityResponse<Win>(response);
  },

  // Get wins by skill
  getBySkill: async (skillId: string, params?: { page?: number; size?: number }): Promise<PaginatedResponse<Win>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    try {
      const response = await skillKnowledgeApiService.get<unknown>(
        `/api/v1/wins/skill/${skillId}${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Win>(response);
    } catch (error: unknown) {
      console.error('[winApi.getBySkill] Error fetching wins:', error);
      throw error;
    }
  },
};

export { skillKnowledgeApiService };
export type { ApiError };

